import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import FlightCard from '../components/FlightCard';
import SearchForm from '../components/SearchForm';
import SEO from '../components/SEO';

/* ────────────────────────────────────────────────────────────
   Skeleton loader
──────────────────────────────────────────────────────────── */
function Skeleton() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
      <div className="h-1 bg-gradient-to-r from-blue-200 to-indigo-200 w-full" />
      <div className="p-5 flex flex-col lg:flex-row gap-5 items-center">
        <div className="flex items-center gap-3 min-w-[160px]">
          <div className="w-12 h-12 rounded-xl bg-gray-100" />
          <div className="space-y-2">
            <div className="h-3 w-28 bg-gray-100 rounded" />
            <div className="h-2 w-16 bg-gray-100 rounded" />
          </div>
        </div>
        <div className="flex-grow flex items-center gap-4">
          <div className="h-9 w-16 bg-gray-100 rounded-lg" />
          <div className="flex-grow h-0.5 bg-gray-100 rounded" />
          <div className="h-9 w-16 bg-gray-100 rounded-lg" />
        </div>
        <div className="space-y-2 min-w-[140px]">
          <div className="h-9 w-24 bg-gray-100 rounded-lg ml-auto" />
          <div className="h-8 w-20 bg-gray-100 rounded-lg ml-auto" />
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Price summary strip (cheapest / avg / fastest)
──────────────────────────────────────────────────────────── */
function PriceSummary({ flights }) {
  if (!flights.length) return null;
  const prices = flights.map(f => f.price);
  const cheapest = Math.min(...prices);
  const avg = Math.round(prices.reduce((a, b) => a + b, 0) / prices.length);
  const fastest = flights.reduce((a, b) => (a.durationMinutes || 999) < (b.durationMinutes || 999) ? a : b);

  return (
    <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4 sm:mb-5">
      {[
        { label: 'Cheapest', value: `$${cheapest.toLocaleString('en-US')}`, icon: '💰', color: 'text-green-600', bg: 'bg-green-50 border-green-200' },
        { label: 'Average', value: `$${avg.toLocaleString('en-US')}`, icon: '📊', color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' },
        { label: 'Fastest', value: fastest.duration || '—', icon: '⚡', color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
      ].map(item => (
        <div key={item.label} className={`border rounded-xl p-3 text-center ${item.bg}`}>
          <p className="text-lg mb-0.5">{item.icon}</p>
          <p className={`text-base font-extrabold ${item.color}`}>{item.value}</p>
          <p className="text-xs text-gray-500 mt-0.5">{item.label}</p>
        </div>
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Filter sidebar
──────────────────────────────────────────────────────────── */
function FilterSidebar({ flights, filters, onChange }) {
  const airlines = useMemo(() => [...new Set(flights.map(f => f.airline || f.airlineCode))], [flights]);
  const prices = flights.map(f => f.price);
  const minP = Math.min(...prices);
  const maxP = Math.max(...prices);

  return (
    <aside className="w-full lg:w-60 shrink-0">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm sticky top-[72px]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <span className="font-bold text-gray-800 text-sm">Filters</span>
          <button
            onClick={() => onChange({ stops: 'all', depTime: 'all', maxPrice: null, hiddenAirlines: [] })}
            className="text-xs text-blue-500 hover:text-blue-700 font-medium"
          >Reset</button>
        </div>

        <div className="p-4 space-y-5">
          {/* Stops */}
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Stops</p>
            <div className="flex flex-col gap-1.5">
              {[['all', 'Any'], ['nonstop', 'Non-stop'], ['stop', 'With stops']].map(([val, label]) => (
                <label key={val} className="flex items-center gap-2 cursor-pointer group">
                  <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                    filters.stops === val ? 'border-blue-600 bg-blue-600' : 'border-gray-300 group-hover:border-blue-400'
                  }`}>
                    {filters.stops === val && <span className="w-1.5 h-1.5 bg-white rounded-full" />}
                  </span>
                  <input type="radio" name="stops" value={val} checked={filters.stops === val}
                    onChange={() => onChange({ ...filters, stops: val })} className="sr-only" />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Departure time */}
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Departure</p>
            <div className="grid grid-cols-2 gap-1.5">
              {[['all','Any'],['morning','Morning'],['afternoon','Afternoon'],['evening','Evening']].map(([val, label]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => onChange({ ...filters, depTime: val })}
                  className={`text-xs py-1.5 rounded-lg border font-medium transition-all ${
                    filters.depTime === val
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-blue-400'
                  }`}
                >{label}</button>
              ))}
            </div>
          </div>

          {/* Price slider */}
          {flights.length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Max Price</p>
              <input type="range" min={minP} max={maxP}
                value={filters.maxPrice ?? maxP}
                onChange={e => onChange({ ...filters, maxPrice: Number(e.target.value) })}
                className="w-full accent-blue-600"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>${minP.toLocaleString('en-US')}</span>
                <span className="font-bold text-blue-600">${(filters.maxPrice ?? maxP).toLocaleString('en-US')}</span>
                <span>${maxP.toLocaleString('en-US')}</span>
              </div>
            </div>
          )}

          {/* Airlines */}
          {airlines.length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Airlines</p>
              <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                {airlines.map(a => {
                  const checked = !(filters.hiddenAirlines || []).includes(a);
                  return (
                    <label key={a} className="flex items-center gap-2 cursor-pointer group">
                      <span className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                        checked ? 'bg-blue-600 border-blue-600' : 'border-gray-300 group-hover:border-blue-400'
                      }`}>
                        {checked && <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/></svg>}
                      </span>
                      <input type="checkbox" checked={checked} className="sr-only"
                        onChange={e => {
                          const hidden = filters.hiddenAirlines || [];
                          onChange({ ...filters, hiddenAirlines: e.target.checked ? hidden.filter(x => x !== a) : [...hidden, a] });
                        }}
                      />
                      <span className="text-sm text-gray-700 truncate">{a}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

/* ────────────────────────────────────────────────────────────
   Hero route bar
──────────────────────────────────────────────────────────── */
function RouteBar({ origin, destination, date, returnDate, adults, children, infants, travelClass, tripType, dataSource, flightCount }) {
  const paxLabel = [
    `${adults} ADT`,
    children > 0 ? `${children} CHD` : null,
    infants > 0 ? `${infants} INF` : null,
  ].filter(Boolean).join(', ');

  return (
    <div className="bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-600 text-white">
      <div className="max-w-7xl mx-auto px-4 py-4 flex flex-wrap items-center gap-3">
        {/* Route */}
        <div className="flex items-center gap-2 text-lg font-extrabold tracking-wide">
          <span className="bg-white/20 px-2 py-0.5 rounded-lg text-white">{origin}</span>
          <span className="text-white/60">{tripType === 'roundtrip' ? '⇄' : '→'}</span>
          <span className="bg-white/20 px-2 py-0.5 rounded-lg text-white">{destination}</span>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm text-white/80">
          <span className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
            {date}{returnDate ? ` – ${returnDate}` : ''}
          </span>
          <span className="text-white/40">·</span>
          <span className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0"/></svg>
            {paxLabel}
          </span>
          <span className="text-white/40">·</span>
          <span className="capitalize">{travelClass}</span>
        </div>

        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {flightCount > 0 && (
            <span className="bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full">
              {flightCount} flights
            </span>
          )}
          {dataSource && (
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${
              dataSource === 'live' ? 'bg-green-400/30 text-green-100' : 'bg-yellow-400/30 text-yellow-100'
            }`}>
              {dataSource === 'live' ? '🟢 Live' : '🟡 Demo'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Sort tabs
──────────────────────────────────────────────────────────── */
const SORT_OPTIONS = [
  { value: 'price',     label: 'Best Price',   icon: '💰' },
  { value: 'duration',  label: 'Shortest',     icon: '⚡' },
  { value: 'departure', label: 'Earliest',     icon: '🌅' },
];

/* ────────────────────────────────────────────────────────────
   Main page
──────────────────────────────────────────────────────────── */
export default function FlightResultsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const origin      = searchParams.get('origin') || '';
  const destination = searchParams.get('destination') || '';
  const date        = searchParams.get('date') || '';
  const returnDate  = searchParams.get('returnDate') || '';
  const adults      = parseInt(searchParams.get('adults'))   || parseInt(searchParams.get('passengers')) || 1;
  const children    = parseInt(searchParams.get('children')) || 0;
  const infants     = parseInt(searchParams.get('infants'))  || 0;
  const passengers  = adults + children + infants;
  const travelClass = searchParams.get('class') || 'economy';
  const tripType    = searchParams.get('tripType') || 'oneway';

  const [flights,    setFlights]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [dataSource, setDataSource] = useState('');
  const [sortBy,     setSortBy]     = useState('price');
  const [showSearch, setShowSearch] = useState(false);
  const [filters,    setFilters]    = useState({ stops: 'all', depTime: 'all', maxPrice: null, hiddenAirlines: [] });

  const hasSearch = origin && destination && date;

  useEffect(() => {
    if (!hasSearch) return;
    setLoading(true);
    setError('');
    setFlights([]);
    axios.get('/api/flights/search', {
      params: { origin, destination, date, returnDate, adults, children, infants, passengers, class: travelClass }
    })
      .then(res => { setFlights(res.data.flights || []); setDataSource(res.data.source || ''); })
      .catch(err => setError(err.response?.data?.error || 'Failed to fetch flights.'))
      .finally(() => setLoading(false));
  }, [origin, destination, date, returnDate, adults, children, infants, travelClass]); // eslint-disable-line

  /* filter + sort + badge */
  const processedFlights = useMemo(() => {
    const maxP = filters.maxPrice ?? Infinity;
    let list = flights.filter(f => {
      if (filters.stops === 'nonstop' && f.stops !== 0) return false;
      if (filters.stops === 'stop'    && f.stops === 0) return false;
      if (f.price > maxP) return false;
      if ((filters.hiddenAirlines || []).includes(f.airline || f.airlineCode)) return false;
      if (filters.depTime !== 'all') {
        const h = parseInt((f.departureTime || '0:00').split(':')[0], 10);
        if (filters.depTime === 'morning'   && (h < 6 || h >= 12)) return false;
        if (filters.depTime === 'afternoon' && (h < 12 || h >= 18)) return false;
        if (filters.depTime === 'evening'   && h < 18) return false;
      }
      return true;
    });

    list = [...list].sort((a, b) => {
      if (sortBy === 'price')     return a.price - b.price;
      if (sortBy === 'duration')  return (a.durationMinutes || 0) - (b.durationMinutes || 0);
      if (sortBy === 'departure') return (a.departureTime || '').localeCompare(b.departureTime || '');
      return 0;
    });

    if (list.length > 0) {
      const cheapestId = [...list].sort((a, b) => a.price - b.price)[0].id;
      const fastestId  = [...list].sort((a, b) => (a.durationMinutes || 0) - (b.durationMinutes || 0))[0].id;
      return list.map(f => ({ ...f, _isCheapest: f.id === cheapestId, _isFastest: f.id === fastestId }));
    }
    return list;
  }, [flights, filters, sortBy]);

  const seoTitle = hasSearch
    ? `Flights from ${origin} to ${destination} on ${date}`
    : 'Search Flights';
  const seoDesc = hasSearch
    ? `Compare ${flights.length || ''} flights from ${origin} to ${destination} on ${date}. Find the cheapest fares starting from $${flights.length ? Math.min(...flights.map(f => f.price)).toFixed(0) : '—'} on USAFlightsDeals.`
    : 'Search and compare flights across all major US airlines. Find the best fares and book instantly on USAFlightsDeals.';

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={seoTitle}
        description={seoDesc}
        noindex={true}
      />

      {/* ── Route hero bar ── */}
      {hasSearch && (
        <RouteBar
          origin={origin} destination={destination}
          date={date} returnDate={returnDate}
          adults={adults} children={children} infants={infants}
          travelClass={travelClass} tripType={tripType}
          dataSource={dataSource}
          flightCount={processedFlights.length}
        />
      )}

      {/* ── Modify search toggle ── */}
      <div className="bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <button
            type="button"
            onClick={() => setShowSearch(s => !s)}
            className="flex items-center gap-2 py-3 text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors"
          >
            <svg className={`w-4 h-4 transition-transform ${showSearch ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            {showSearch ? 'Hide search' : '✏️ Modify search'}
          </button>
          {showSearch && (
            <div className="pb-5">
              <SearchForm initialValues={{ origin, destination, date, returnDate, adults, children, infants, class: travelClass, tripType }} />
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">

        {/* ── No search prompt ── */}
        {!hasSearch && (
          <div className="max-w-xl mx-auto mt-16 text-center">
            <div className="text-6xl mb-5">✈️</div>
            <h2 className="text-2xl font-extrabold text-gray-800 mb-2">Find your perfect flight</h2>
            <p className="text-gray-500 mb-6">Use the search form to discover flights worldwide.</p>
            <button onClick={() => navigate('/')} className="bg-blue-600 text-white font-semibold px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors">
              ← Back to Home
            </button>
          </div>
        )}

        {hasSearch && (
          <div className="flex flex-col lg:flex-row gap-5">

            {/* ── Filter sidebar ── */}
            {!loading && flights.length > 0 && (
              <FilterSidebar flights={flights} filters={filters} onChange={setFilters} />
            )}

            {/* ── Main results column ── */}
            <div className="flex-grow min-w-0 space-y-4">

              {/* Sort tabs + count */}
              {!loading && processedFlights.length > 0 && (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <p className="text-sm text-gray-500 font-medium">
                    Showing <span className="font-bold text-gray-800">{processedFlights.length}</span> of {flights.length} flights
                  </p>
                  <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1">
                    {SORT_OPTIONS.map(o => (
                      <button
                        key={o.value}
                        onClick={() => setSortBy(o.value)}
                        className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg transition-all ${
                          sortBy === o.value
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'text-gray-500 hover:text-blue-600'
                        }`}
                      >
                        <span>{o.icon}</span> {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Price summary */}
              {!loading && flights.length > 0 && <PriceSummary flights={flights} />}

              {/* Skeletons */}
              {loading && [...Array(5)].map((_, i) => <Skeleton key={i} />)}

              {/* Error */}
              {!loading && error && (
                <div className="bg-red-50 border border-red-100 rounded-2xl p-10 text-center">
                  <div className="text-5xl mb-3">⚠️</div>
                  <p className="font-bold text-red-600 text-lg">{error}</p>
                  <p className="text-sm text-red-400 mt-1">Try a different route or date.</p>
                </div>
              )}

              {/* Filtered empty */}
              {!loading && !error && flights.length > 0 && processedFlights.length === 0 && (
                <div className="bg-white border border-gray-100 rounded-2xl p-12 text-center">
                  <div className="text-5xl mb-4">🔍</div>
                  <p className="font-bold text-gray-700 text-lg">No flights match your filters</p>
                  <button
                    onClick={() => setFilters({ stops: 'all', depTime: 'all', maxPrice: null, hiddenAirlines: [] })}
                    className="mt-4 bg-blue-600 text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-blue-700 transition-colors"
                  >Reset all filters</button>
                </div>
              )}

              {/* No results at all */}
              {!loading && !error && hasSearch && flights.length === 0 && (
                <div className="bg-white border border-gray-100 rounded-2xl p-12 text-center">
                  <div className="text-5xl mb-4">😕</div>
                  <p className="font-bold text-gray-700 text-lg">No flights found for this route</p>
                  <p className="text-sm text-gray-400 mt-1">Try different dates or nearby airports.</p>
                </div>
              )}

              {/* Flight cards */}
              {!loading && !error && processedFlights.length > 0 && (
                <>
                  {processedFlights.map(flight => (
                    <FlightCard
                      key={flight.id}
                      flight={flight}
                      passengers={passengers}
                      travelClass={travelClass}
                    />
                  ))}
                  <p className="text-center text-xs text-gray-400 py-3">
                    — End of results · {processedFlights.length} flights shown —
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
